import { BadgeCheck, Inbox } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaCheckbox } from "@/shared/components/forms/ColocaCheckbox";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";

import { ITEMS_PAGE_SIZE } from "../../constants/payoutBatches.constants";
import type { UsePayoutBatchSelectionResult } from "../../hooks/usePayoutBatchSelection";
import {
  type PayoutBatchDetail,
  type PayoutBatchItem,
  PayoutBatchItemStatus,
} from "../../types/payoutBatches.types";
import { batchBeneficiary } from "../../utils/batchBeneficiary";
import { maskBatchDestination } from "../../utils/batchDestination";
import { payoutActivityPath } from "../../utils/batchLinks";
import { ItemStatusPill } from "../detail/ItemStatusPill";

export type BatchItemsTableMode = "review" | "execution";

export interface BatchItemsTableProps {
  mode: BatchItemsTableMode;
  batch: PayoutBatchDetail;
  rows: PayoutBatchItem[];
  isLoading: boolean;
  page: number;
  total: number;
  onPageChange: (page: number) => void;
  onRowClick: (item: PayoutBatchItem) => void;
  /** Review mode only: the optimistic selection the checkboxes read and write. */
  selection?: Pick<UsePayoutBatchSelectionResult, "isSelected" | "toggle">;
  selectionDisabled?: boolean;
}

const isSelectable = (item: PayoutBatchItem): boolean =>
  item.status === PayoutBatchItemStatus.SELECTED || item.status === PayoutBatchItemStatus.EXCLUDED;

/**
 * The rows of a batch, one server-side page at a time. In review mode a checkbox column drives the selection
 * (rows with errors cannot be selected and read as secondary); in execution mode the columns tell what
 * happened to each row and link to the payout it became.
 */
export function BatchItemsTable({
  mode,
  batch,
  rows,
  isLoading,
  page,
  total,
  onPageChange,
  onRowClick,
  selection,
  selectionDisabled = false,
}: BatchItemsTableProps) {
  const { t } = useTranslations("payoutBatches");
  const delivery = batch.currency;
  const source = batch.source_currency;
  const crossCurrency = source !== delivery;

  const selectableRows = rows.filter(isSelectable);
  const selectedCount = selection ? selectableRows.filter((row) => selection.isSelected(row)).length : 0;
  const allSelected = selectableRows.length > 0 && selectedCount === selectableRows.length;

  const columns = useMemo<ColocaDataTableColumn<PayoutBatchItem>[]>(() => {
    const beneficiary: ColocaDataTableColumn<PayoutBatchItem> = {
      id: "beneficiary",
      header: t("items_col_beneficiary"),
      cell: (row) => {
        const { name, fromDirectory, directoryHolder } = batchBeneficiary(row);
        return (
          <div className="min-w-0">
            <p className="flex max-w-56 items-center gap-1 font-medium text-foreground">
              <span className="truncate">{name ?? t("row_beneficiary_unknown")}</span>
              {fromDirectory && (
                <span title={t("row_beneficiary_from_directory")} className="inline-flex shrink-0">
                  <BadgeCheck aria-hidden="true" className="size-3.5 text-success" />
                  <span className="sr-only">{t("row_beneficiary_from_directory")}</span>
                </span>
              )}
            </p>
            <p className="flex max-w-56 gap-1 text-xs text-muted-foreground">
              <span className="shrink-0 font-mono tabular-nums">{maskBatchDestination(row.destination)}</span>
              {directoryHolder && <span className="truncate">· {directoryHolder}</span>}
            </p>
          </div>
        );
      },
    };
    const receive: ColocaDataTableColumn<PayoutBatchItem> = {
      id: "receive",
      header: t("items_col_receive"),
      headerClassName: "text-right",
      cellClassName: "text-right",
      cell: (row) =>
        row.receive_amount ? (
          <ColocaAmountDisplay
            value={row.receive_amount}
            currency={delivery}
            fractionDigits={minorUnitsFor(delivery)}
            size="sm"
          />
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    };
    const debit: ColocaDataTableColumn<PayoutBatchItem> = {
      id: "debit",
      header: t("items_col_debit"),
      headerClassName: "text-right",
      cellClassName: "text-right",
      cell: (row) =>
        row.pricing ? (
          <ColocaAmountDisplay
            value={row.pricing.total_debit_source}
            currency={source}
            fractionDigits={minorUnitsFor(source)}
            size="sm"
          />
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    };
    const status: ColocaDataTableColumn<PayoutBatchItem> = {
      id: "status",
      header: t("items_col_status"),
      cell: (row) => <ItemStatusPill status={row.status} />,
    };
    const rowNumber: ColocaDataTableColumn<PayoutBatchItem> = {
      id: "row",
      header: t("items_col_row"),
      headerClassName: "w-14",
      cell: (row) => (
        <span className="font-mono text-xs text-muted-foreground tabular-nums">{row.row_number}</span>
      ),
    };

    if (mode === "execution") {
      return [
        rowNumber,
        beneficiary,
        receive,
        debit,
        status,
        {
          id: "reason",
          header: t("items_col_reason"),
          cellClassName: "max-w-64",
          cell: (row) =>
            row.failure_message ? (
              <span className="block truncate text-xs text-muted-foreground" title={row.failure_message}>
                {row.failure_message}
              </span>
            ) : (
              <span className="text-muted-foreground">—</span>
            ),
        },
        {
          id: "payout",
          header: t("items_col_payout"),
          cell: (row) =>
            row.payout_id ? (
              <Link
                to={payoutActivityPath(row.payout_id)}
                className="text-xs font-medium text-primary underline-offset-2 hover:underline"
              >
                {t("row_payout_link")}
              </Link>
            ) : (
              <span className="text-muted-foreground">—</span>
            ),
        },
      ];
    }

    const select: ColocaDataTableColumn<PayoutBatchItem> = {
      id: "select",
      headerClassName: "w-10",
      header: selection ? (
        <ColocaCheckbox
          aria-label={t("items_select_page")}
          checked={allSelected}
          indeterminate={selectedCount > 0 && !allSelected}
          disabled={selectionDisabled || selectableRows.length === 0}
          onCheckedChange={(checked) => {
            for (const row of selectableRows) {
              if (selection.isSelected(row) !== checked) selection.toggle(row, checked);
            }
          }}
        />
      ) : null,
      cell: (row) =>
        selection && isSelectable(row) ? (
          <ColocaCheckbox
            aria-label={t("items_select_row", { row: row.row_number })}
            checked={selection.isSelected(row)}
            disabled={selectionDisabled}
            onCheckedChange={(checked) => selection.toggle(row, checked)}
          />
        ) : null,
    };
    const fee: ColocaDataTableColumn<PayoutBatchItem> = {
      id: "fee",
      header: t("items_col_fee"),
      headerClassName: "text-right",
      cellClassName: "text-right",
      cell: (row) =>
        row.pricing ? (
          <ColocaAmountDisplay
            value={row.pricing.fee_total}
            currency={delivery}
            fractionDigits={minorUnitsFor(delivery)}
            size="sm"
          />
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    };
    const rate: ColocaDataTableColumn<PayoutBatchItem> = {
      id: "rate",
      header: t("items_col_rate"),
      headerClassName: "text-right",
      cellClassName: "text-right",
      cell: (row) => (
        <span className="font-mono text-xs text-muted-foreground tabular-nums">
          {row.pricing?.rate ?? "—"}
        </span>
      ),
    };
    const issues: ColocaDataTableColumn<PayoutBatchItem> = {
      id: "issues",
      header: t("items_col_issues"),
      cell: (row) => (
        <span className="flex flex-wrap gap-1">
          {row.errors.length > 0 && (
            <ColocaBadge variant="destructive">
              {t("items_errors_count", { count: row.errors.length })}
            </ColocaBadge>
          )}
          {row.warnings.length > 0 && (
            <ColocaBadge variant="warning">
              {t("items_warnings_count", { count: row.warnings.length })}
            </ColocaBadge>
          )}
        </span>
      ),
    };

    return [
      select,
      rowNumber,
      beneficiary,
      receive,
      fee,
      debit,
      ...(crossCurrency ? [rate] : []),
      status,
      issues,
    ];
  }, [
    allSelected,
    crossCurrency,
    delivery,
    mode,
    selectableRows,
    selectedCount,
    selection,
    selectionDisabled,
    source,
    t,
  ]);

  return (
    <ColocaDataTable
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      onRowClick={onRowClick}
      loading={isLoading}
      skeletonRows={8}
      minWidth={mode === "review" ? 1040 : 960}
      rowClassName={(row) =>
        cn(
          row.status === PayoutBatchItemStatus.INVALID && "bg-destructive/5 text-muted-foreground",
          selection && isSelectable(row) && selection.isSelected(row) && "bg-primary/5",
        )
      }
      empty={
        <ColocaEmpty
          icon={Inbox}
          title={t("items_empty_title")}
          description={t("items_empty_description")}
          surface="card"
        />
      }
      footer={
        total > ITEMS_PAGE_SIZE ? (
          <ColocaPagination
            page={page}
            perPage={ITEMS_PAGE_SIZE}
            total={total}
            withSeparator={false}
            size="sm"
            onPageChange={onPageChange}
          />
        ) : undefined
      }
    />
  );
}
